import React, { useState, useEffect } from 'react';
import Spinner from '../Spinner';
import { useApi } from '../../api/ApiProvider';
import Api from '../../axiosApi/api';
import { useToastMessageQueue } from '../ToastMessages/ToastMessageProvider';
import { getUserEntity } from 'common/UserEntityProvider';
import intl from 'components/i18n/ReactIntlWrapper';
import ItemsSelect from '../ItemsSelect';
import InvoicesNavbar from './InvoicesNavbar';
import InvoicesList from './InvoicesList';
import InvoicePagination from './InvoicesPagination';
import { handleAPIError } from 'common/errorHandler';
import { formatDateFilterInvoices } from '../../common/utils';
import ErrorSummary from 'components/common/ErrorSummary';
import { DateRange, ENTITY_TYPE } from 'common/constants';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PlusButton from 'components/common/PlusButton';

export type InvoicesInput = {
    to: string | null,
    number: string | null,
    date: string | null,
    from: string | null,
    range: DateRange | undefined,
    amount: number | null,
    amounts: object,
    idInvoiceType: number | null,
    idWorkflowState: number | null,
    queryTerm?: string | null,
    currentPage?: number,
    sortValue?: string | null,
    orderValue?: string| null,
};

export type ValidatingInput = {
    idInvoiceType: number | null,
    idWorkflowState: number | null,
    queryTerm?: string | null,
    currentPage?: number,
    sortValue?: string | null,
    orderValue?: string| null,
};

export type SearchResult = {
	totalRecords: number | null,
	queryResult: any
};
interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
  }

const Invoices = ({ OwnerView = false }) => {
    const [validatingInput, setValidatingInput] = useState<ValidatingInput | null>({
        idInvoiceType: null,
        idWorkflowState: null,
        queryTerm: "",
        currentPage: 1,
		sortValue: "",
		orderValue: "asc",
	});

    const [input, setInput] = useState<InvoicesInput | null>({
        to: "",
        number: "",
        date: "",
        from: "",
        range: undefined,
        amount: null,
        amounts: {},
        idInvoiceType: null,
        idWorkflowState: null,
        queryTerm: "",
        currentPage: 1,
		sortValue: "",
		orderValue: "asc",
	});

    useEffect(() => {
        if (input.from && input.to) {
            setInput(prev => ({
                ...prev,
                range: {
                    from: new Date(input.from),
                    to: new Date(input.to),
                }
            }));
        }
    }, [input.from, input.to]);

    const [invoices, setInvoices] = useState<SearchResult | null>({
        totalRecords: null,
        queryResult: null
	});

    const [invoicesOwner, setInvoicesOwner] = useState<SearchResult | null>({
        totalRecords: null,
        queryResult: null
	});

    const [validatingInvoices, setValidatingInvoices] = useState<SearchResult | null>({
        totalRecords: null,
        queryResult: null
    });

    const [invoiceStatus, setInvoiceStatus] = useState<SearchResult | null>({
        totalRecords: null,
        queryResult: null
    });
    const [value, setValue] = React.useState(0);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    const [loading, setLoading] = useState<boolean>(false);

    const [loadingValidating, setLoadingValidating] = useState<boolean>(false);

    const [errors, setErrors] = useState({});

    const api: Api = useApi();

    const toast = useToastMessageQueue();

    const entityType = getUserEntity().entityType;

    const [pageCount, setpageCount] = useState<number>(0);

    const [validatingPageCount, setValidatingPageCount] = useState<number>(0);

    const [limit, setLimit] = useState<number>(() => {
		return parseInt(localStorage.getItem('itemsPerPageTimeOff'), 10) || 10;
	});

    useEffect(() => {
		setLimit(parseInt(localStorage.getItem('itemsPerPageTimeOff'), 10) || 10);
	}, []);

    const [validatingLimit, setValidatingLimit] = useState<number>(() => {
        return parseInt(localStorage.getItem('itemsPerPageInvoicesValidating'), 10) || 10;
    });

    useEffect(() => {
		setValidatingLimit(parseInt(localStorage.getItem('itemsPerPageInvoicesValidating'), 10) || 10);
	}, []);

    const fetchInvoices = async (term?: string, pageNum?: number, pageSize?: number, sort?: string, order?: string, f?: Array<string>) => {
        setLoading(true);
        if (input.idWorkflowState) {
            f = [`idworkflowstate:${input.idWorkflowState}`];
        }
        if (input.range?.from) {
            let from = formatDateFilterInvoices(input.range.from);
            f = f || [];
            f.push(`startdate:${from}`);
        }
        if(input.range?.to){
            let to = formatDateFilterInvoices(input.range.to);
            f = f || [];
            f.push(`enddate:${to}`);
        }
        if (Number(input.amounts["min"])){
            f = f || [];
            f.push(`amountabove:${Number(input.amounts["min"])}`);
        }
        if (Number(input.amounts["max"])){
            f = f || [];
            f.push(`amountbelow:${Number(input.amounts["max"])}`);
        }
        const response = await api.invoiceApi.apiVversionInvoiceSearchIssuedGet("1", input.queryTerm, input.currentPage, limit, input.sortValue, input.orderValue, f, {}).then((response) => {
            if (response.data.data.queryResult) {
                setInvoices({
                    totalRecords: response.data?.data?.totalRecords,
                    queryResult: response.data?.data?.queryResult
                });
            } else {
                setInvoices({
                    totalRecords: null,
                    queryResult: null
                });
            };
            console.log("Success");
        }).catch((error) => {
            handleAPIError(error, toast, errors);
            setErrors({ ...errors });
        }).finally(() => {
            setLoading(false);
        })
    };

    const fetchInvoicesOwner = (term?: string, pageNum?: number, pageSize?: number, sort?: string, order?: string, f?: Array<string>) => {
        setLoading(true);
        if (input.idWorkflowState) {
            f = [`idworkflowstate:${input.idWorkflowState}`];
        }
        if (input.range?.from) {
            let from = formatDateFilterInvoices(input.range.from);
            f = f || [];
            f.push(`startdate:${from}`);
        }
        if(input.range?.to){
            let to = formatDateFilterInvoices(input.range.to);
            f = f || [];
            f.push(`enddate:${to}`);
        }
        if (Number(input.amounts["min"])){
            f = f || [];
            f.push(`amountabove:${Number(input.amounts["min"])}`);
        }
        if (Number(input.amounts["max"])){
            f = f || [];
            f.push(`amountbelow:${Number(input.amounts["max"])}`);
        }
        api.invoiceApi.apiVversionInvoiceSearchGet ("1", input.queryTerm, input.currentPage, limit, input.sortValue, input.orderValue, f, {}).then((response) => {
            if (response.data.data.queryResult) {
                setInvoicesOwner({
                    totalRecords: response.data?.data?.totalRecords,
                    queryResult: response.data?.data?.queryResult
                });
            } else {
                setInvoicesOwner({
                    totalRecords: null,
                    queryResult: null
                });
            };
            console.log("Success");
        }).catch((error) => {
            handleAPIError(error, toast, errors);
            setErrors({ ...errors });
        }).finally(() => {
            setLoading(false);
        })
    }

    const fetchInvoicesStatus = async () => {
        const response = await api.workflowApi.apiVversionModuleWorkflowStatesModuleIdGet(4, "1").then((response) => {
        if (response.data.data.queryResult) {
            setInvoiceStatus({
                totalRecords: response.data?.data?.totalRecords,
                queryResult: response.data?.data?.queryResult
            });
        };
        }).catch((error) => {
            handleAPIError(error, toast, errors);
            setErrors({ ...errors });
        });
    };

    const fetchValidatingInvoices = async (moduleId?: number, term?: string, pageNum?: number, pageSize?: number, order?: string, f?: Array<string>) => {
        setLoadingValidating(true);
        if (validatingInput.idWorkflowState) {
            f = [`idworkflowstate:${validatingInput.idWorkflowState}`];
        }
        const response = await api.invoiceApi.apiVversionInvoiceSearchIncomingGet("1", 4, validatingInput.queryTerm, validatingLimit, validatingInput.orderValue).then((response) => {
            if (response.data.data.queryResult) {
                setValidatingInvoices({
                    totalRecords: response.data?.data?.totalRecords,
                    queryResult: response.data?.data?.queryResult
                });
                setLoadingValidating(false);
            } else {
                setValidatingInvoices({
                    totalRecords: null,
                    queryResult: null
                });
                setLoadingValidating(false);
            };
        }).catch((error) => {
            handleAPIError(error, toast, errors);
            setErrors({ ...errors });
            setLoadingValidating(false);
        })
    };

    function CustomTabPanel(props: TabPanelProps) {
        const { children, value, index, ...other } = props;

        return (
          <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
          >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
          </div>
        );
      }
      function a11yProps(index: number) {
        return {
          id: `simple-tab-${index}`,
          'aria-controls': `simple-tabpanel-${index}`,
        };
      }

    useEffect(() => {
        if (OwnerView || entityType === ENTITY_TYPE.CLIENT) {
            fetchInvoicesOwner();
        } else {
            fetchInvoices();
        }
        fetchInvoicesStatus();
	}, [input, limit, OwnerView, entityType]);

    useEffect(() => {
        fetchValidatingInvoices();
	}, [validatingInput, validatingLimit]);

    const handlePageClick = pageNum => {
        if (pageNum >= 1 && pageNum <= pageCount) {
            setInput({ ...input, currentPage: pageNum });
        }
    };

    const handlePageClickValidating = pageNum => {
        if (pageNum >= 1 && pageNum <= validatingPageCount) {
            setValidatingInput({ ...validatingInput, currentPage: pageNum });
        }
    };

    const nextPage = () => {
        if (input.currentPage < pageCount) {
            setInput({ ...input, currentPage: input.currentPage + 1 });
        }
    };

    const nextPageValidating = () => {
        if (validatingInput.currentPage < validatingPageCount) {
            setValidatingInput({ ...validatingInput, currentPage: validatingInput.currentPage + 1 });
        }
    };

    const prevPage = () => {
        if (input.currentPage > 1) {
            setInput({ ...input, currentPage: input.currentPage - 1 });
        }
    };

    const prevPageValidating = () => {
        if (validatingInput.currentPage > 1) {
            setValidatingInput({ ...validatingInput, currentPage: validatingInput.currentPage - 1 });
        }
    };

    const initialPage = () => setInput({ ...input, currentPage: 1 });

    const initialPageValidating = () => setValidatingInput({ ...validatingInput, currentPage: 1 });

    const finalPage = () => setInput({ ...input, currentPage: pageCount });

    const finalPageValidating = () => setValidatingInput({ ...validatingInput, currentPage: validatingPageCount });

    const onSearch = (queryString, idStatus, range, amounts) => {
        setErrors([]);
		setInput({ ...input, queryTerm: queryString, idWorkflowState: idStatus, range: range, currentPage: 1, amounts: amounts });
	};

    const onSearchValidating = (queryString, idStatus, range, amounts) => {
        setErrors([]);
        setValidatingInput({ ...validatingInput, queryTerm: queryString, idWorkflowState: idStatus, currentPage: 1 });
    };

    const path = window.location.pathname.split('/').pop();
    const getTitle = () => {
        if (OwnerView && path === 'accountspayable') {
            return intl.get('mainNavigation.billing.accountsPayable');
        }
        if (!OwnerView && path === 'myInvoices') {
            return intl.get('mainNavigation.invoice.myInvoices');
        }
        return intl.get('mainNavigation.invoice');
    };
    return (
        <div className="container">
            <div className='card mt-4'>
                <div className="card-header">
                    {
                        <h2 className="title">{getTitle()}</h2>
                    }
                </div>
                <div className='card-body'>
                    {(path === "invoicing") ?
                    <div>
                    <InvoicesNavbar
                        invoiceStatus={invoiceStatus?.queryResult}
                        onSearch={onSearch}
                        allInvoices={true}
                        showPlusButton={entityType === ENTITY_TYPE.CORPORATE_ENTITY}
                    />
                    {
                        loading === true ?
                        <Spinner /> :
                        <>
                            <div className="col-md-12">
                                <ErrorSummary errors={errors} showEverything={false} mode="warning"></ErrorSummary>
                            </div>
                            <InvoicesList searchResult={OwnerView || entityType === ENTITY_TYPE.CLIENT ? invoicesOwner : invoices} OwnerView={OwnerView} isValidating={false} />
                            <div className="pagination row w-100">
                                <div className="col-10 col-xl-11 d-flex justify-content-center">
                                    <InvoicePagination handlePageClick={handlePageClick} limit={limit} searchResult={OwnerView || entityType === ENTITY_TYPE.CLIENT ? invoicesOwner : invoices} nextPage={nextPage} prevPage={prevPage} input={input} pageCount={pageCount} setpageCount={setpageCount} initialPage={initialPage} finalPage={finalPage} />
                                </div>
                                <div className="col-2 col-xl-1 d-flex justify-content-end">
                                    <ItemsSelect
                                        input={input}
                                        setInput={setInput}
                                        limit={limit}
                                        setLimit={setLimit}
                                        storageKey="itemsPerPageInvoices"
                                    />
                                </div>
                            </div>
                        </>
                    }
                </div> :
                    <Box sx={{ width: '100%' }}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
                                <Tab label={<div className='d-flex align-items-center gap-2'>
                                    <VisibilityIcon />
                                    {intl.get('mainNavigation.validatingInvoices')}
                                </div>} {...a11yProps(0)} />
                                <Tab label={<div className='d-flex align-items-center gap-2'>
                                    <CalendarMonthIcon />
                                    {intl.get('mainNavigation.allInvoices')}
                                </div>} {...a11yProps(1)} />
                                <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                <Grid item>
                                </Grid>
                                <Grid item sx={{ display: ((entityType === ENTITY_TYPE.CORPORATE_ENTITY && path !== 'invoicing') || entityType === ENTITY_TYPE.CLIENT) ? 'none' : 'flex' }}>
                                    <PlusButton link={entityType === ENTITY_TYPE.CORPORATE_ENTITY ? '/invoicing/newinvoice' : '/myInvoices/upload'} tooltip="invoice.new"/>
                                </Grid>
                                </Grid>
                            </Tabs>
                        </Box>
                        <CustomTabPanel value={value} index={0}>
                            <div>
                            <InvoicesNavbar
                                invoiceStatus={validatingInvoices?.queryResult}
                                allInvoices={false}
                                onSearch={onSearchValidating}
                            />
                            {
                                loading === true ?
                                <Spinner /> :
                                <>
                                    <div className="col-md-12">
                                        <ErrorSummary errors={errors} showEverything={false} mode="warning"></ErrorSummary>
                                    </div>
                                    <InvoicesList searchResult={validatingInvoices} OwnerView={OwnerView} isValidating={true} />
                                    <div className="pagination row w-100">
                                        <div className="col-10 col-xl-11 d-flex justify-content-center">
                                            <InvoicePagination handlePageClick={handlePageClickValidating} limit={validatingLimit} searchResult={validatingInvoices} nextPage={nextPageValidating} prevPage={prevPageValidating} input={validatingInput} pageCount={validatingPageCount} setpageCount={setValidatingPageCount} initialPage={initialPageValidating} finalPage={finalPageValidating} />
                                        </div>
                                        <div className="col-2 col-xl-1 d-flex justify-content-end">
                                            <ItemsSelect
                                                input={validatingInput}
                                                setInput={setValidatingInput}
                                                limit={validatingLimit}
                                                setLimit={setValidatingLimit}
                                                storageKey="itemsPerPageInvoicesValidating"
                                            />
                                        </div>
                                    </div>
                                </>
                            }
                        </div>
                        </CustomTabPanel>
                        <CustomTabPanel value={value} index={1}>
                            <div>
                                <InvoicesNavbar
                                    invoiceStatus={invoiceStatus?.queryResult}
                                    onSearch={onSearch}
                                    allInvoices={true}
                                />
                                {
                                    loading === true ?
                                    <Spinner /> :
                                    <>
                                        <div className="col-md-12">
                                            <ErrorSummary errors={errors} showEverything={false} mode="warning"></ErrorSummary>
                                        </div>
                                        <InvoicesList searchResult={OwnerView ? invoicesOwner : invoices} OwnerView={OwnerView} isValidating={false} />
                                        <div className="pagination row w-100">
                                            <div className="col-10 col-xl-11 d-flex justify-content-center">
                                                <InvoicePagination handlePageClick={handlePageClick} limit={limit} searchResult={OwnerView ? invoicesOwner : invoices} nextPage={nextPage} prevPage={prevPage} input={input} pageCount={pageCount} setpageCount={setpageCount} initialPage={initialPage} finalPage={finalPage} />
                                            </div>
                                            <div className="col-2 col-xl-1 d-flex justify-content-end">
                                                <ItemsSelect
                                                    input={input}
                                                    setInput={setInput}
                                                    limit={limit}
                                                    setLimit={setLimit}
                                                    storageKey="itemsPerPageInvoices"
                                                />
                                            </div>
                                        </div>
                                    </>
                                }
                            </div>
                        </CustomTabPanel>
                    </Box>
}
                </div>
            </div>
        </div>
    )
}

export default Invoices;
